/**
 * /Recon — Action Tracker.
 *
 * Distilled "what do I need to do" view, separate from the report
 * read. Same data source as the report viewer (signed JSON payload +
 * finding-actions ledger), but flattened into one cross-section list
 * with state-summary stats, filter pills, and an expandable Take
 * Action panel per finding.
 *
 * The point of carving this out: the report is a narrative the buyer
 * reads once; the action tracker is a work list they come back to as
 * remediation progresses. They want different ergonomics.
 *
 * URL: /portal/engagements/:caseId/reports/:reportId/actions
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import {
  getReportPayloadApiV1ClientPortalEngagementsCaseIdReportsReportIdPayloadGet,
  listFindingActionsApiV1ClientPortalEngagementsCaseIdFindingActionsGet,
  requestFindingVerificationApiV1ClientPortalFindingsEvidenceCardIdVerifyFixPost,
} from "@hackereyes/api-client";
import { TakeActionPanel, type ActionState } from "./take-action-panel";
import { CommentThread } from "./comment-thread";
import "./action-tracker.css";

interface PayloadCard {
  id: string;
  surface_id?: string;
  title?: string;
  description?: string;
  severity?: string;
  category?: string;
  finding_type?: string;
  target_display?: string;
  target_value?: string;
  report_section?: string;
}

interface PayloadSection {
  key: string;
  title: string;
  cards?: PayloadCard[];
}

interface PayloadReport {
  title?: string;
}

interface PayloadCase {
  case_number?: string;
}

interface ReportPayload {
  report: PayloadReport;
  case: PayloadCase;
  sections: PayloadSection[];
}

interface FindingAction {
  evidence_card_id: string;
  state: ActionState;
  note: string | null;
  verified_at?: string | null;
  verification_tool_run_id?: string | null;
  fix_by_date?: string | null;
}

const ACTIONABLE_SEVERITIES = new Set(["critical", "high", "medium", "low"]);
const SEVERITY_RANK: Record<string, number> = {
  critical: 0, high: 1, medium: 2, low: 3, informational: 4,
};
const SEVERITY_LABEL: Record<string, string> = {
  critical: "Critical", high: "High", medium: "Medium", low: "Low",
  informational: "Info",
};

type Category = "all" | "security" | "performance" | "accessibility";

const STATE_LABEL: Record<ActionState, string> = {
  acknowledged: "Acknowledged",
  in_progress: "In progress",
  fixed: "Fixed",
  accepted_risk: "Accept risk",
  needs_help: "Needs help",
};

function categoryOf(card: PayloadCard): Exclude<Category, "all"> {
  const c = (card.category || "Security").toLowerCase();
  if (c === "performance") return "performance";
  if (c === "accessibility") return "accessibility";
  return "security";
}

export function ActionTrackerPage() {
  const { caseId, reportId } = useParams<{ caseId: string; reportId: string }>();
  const [category, setCategory] = useState<Category>("all");

  const { data: payload, isLoading: payloadLoading, error: payloadError } = useQuery({
    queryKey: ["report-payload", caseId, reportId],
    enabled: !!(caseId && reportId),
    queryFn: async () => {
      const { data, error } =
        await getReportPayloadApiV1ClientPortalEngagementsCaseIdReportsReportIdPayloadGet({
          path: { case_id: caseId!, report_id: reportId! },
        });
      if (error) throw new Error("Failed to load report.");
      return data as unknown as ReportPayload;
    },
  });

  const { data: actions } = useQuery({
    queryKey: ["finding-actions", caseId],
    enabled: !!caseId,
    queryFn: async () => {
      const { data, error } =
        await listFindingActionsApiV1ClientPortalEngagementsCaseIdFindingActionsGet({
          path: { case_id: caseId! },
        });
      if (error) throw new Error("Failed to load actions.");
      return (data ?? []) as FindingAction[];
    },
  });

  const actionsByCardId = useMemo(() => {
    const map = new Map<string, FindingAction>();
    for (const a of actions ?? []) map.set(a.evidence_card_id, a);
    return map;
  }, [actions]);

  // Flatten: every actionable card across every section, with its section
  // attached (for context display).
  const items = useMemo(() => {
    if (!payload) return [];
    const out: Array<{ card: PayloadCard; sectionTitle: string }> = [];
    for (const sec of payload.sections ?? []) {
      for (const card of sec.cards ?? []) {
        const sev = (card.severity || "informational").toLowerCase();
        if (!ACTIONABLE_SEVERITIES.has(sev)) continue;
        out.push({ card, sectionTitle: sec.title });
      }
    }
    out.sort((a, b) => {
      const sa = SEVERITY_RANK[(a.card.severity || "low").toLowerCase()] ?? 99;
      const sb = SEVERITY_RANK[(b.card.severity || "low").toLowerCase()] ?? 99;
      if (sa !== sb) return sa - sb;
      return (a.card.title || "").localeCompare(b.card.title || "");
    });
    return out;
  }, [payload]);

  const filtered = useMemo(() => {
    if (category === "all") return items;
    return items.filter(({ card }) => categoryOf(card) === category);
  }, [items, category]);

  // Aggregate state counts — across the FULL actionable list (not the
  // filtered view), so the buyer's progress bar reflects the engagement
  // as a whole.
  const counts = useMemo(() => {
    const c = {
      total: items.length,
      todo: 0,
      acknowledged: 0,
      in_progress: 0,
      fixed: 0,
      accepted_risk: 0,
      needs_help: 0,
    };
    for (const { card } of items) {
      const a = actionsByCardId.get(card.id);
      if (!a) c.todo++;
      else c[a.state]++;
    }
    return c;
  }, [items, actionsByCardId]);

  // Counts per category for the pill labels.
  const categoryCounts = useMemo(() => {
    const out: Record<Exclude<Category, "all">, number> = {
      security: 0, performance: 0, accessibility: 0,
    };
    for (const { card } of items) out[categoryOf(card)]++;
    return out;
  }, [items]);

  if (payloadLoading) {
    return (
      <div className="action-tracker">
        <div className="action-tracker-inner">
          <p className="muted">Loading action items…</p>
        </div>
      </div>
    );
  }

  if (payloadError || !payload) {
    return (
      <div className="action-tracker">
        <div className="action-tracker-inner">
          <Link to={`/portal/engagements/${caseId}`} className="action-tracker-back">
            ← Back to engagement
          </Link>
          <div className="action-tracker-error">
            Couldn't load this report's findings. Refresh or open the
            engagement page.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="action-tracker">
      <div className="action-tracker-inner">
        <Link to={`/portal/engagements/${caseId}`} className="action-tracker-back">
          ← Back to engagement
        </Link>

        <header className="action-tracker-header">
          <div className="eyebrow">
            {payload.case.case_number} · Action items
          </div>
          <h1 className="action-tracker-title">
            {counts.total === 0
              ? "Nothing to action."
              : `${counts.total} action item${counts.total === 1 ? "" : "s"}`}
          </h1>
          {counts.total > 0 && (
            <p className="action-tracker-lede">
              The findings below need a remediation decision. Mark each as
              you work through them — your state choices stay attached to
              the finding for re-tests and follow-up engagements.
            </p>
          )}
        </header>

        {counts.total > 0 && (
          <>
            <div className="action-tracker-stats">
              <Stat label="To do" value={counts.todo + counts.acknowledged} tone="slate" />
              <Stat label="In progress" value={counts.in_progress} tone="cyan" />
              <Stat label="Fixed" value={counts.fixed} tone="green" />
              <Stat label="Accepted risk" value={counts.accepted_risk} tone="amber" />
              <Stat label="Needs help" value={counts.needs_help} tone="flame" />
            </div>

            <div className="action-tracker-filters" role="tablist">
              <FilterPill
                active={category === "all"}
                onClick={() => setCategory("all")}
                label={`All (${counts.total})`}
              />
              <FilterPill
                active={category === "security"}
                onClick={() => setCategory("security")}
                label={`Security (${categoryCounts.security})`}
              />
              <FilterPill
                active={category === "performance"}
                onClick={() => setCategory("performance")}
                label={`Performance (${categoryCounts.performance})`}
              />
              <FilterPill
                active={category === "accessibility"}
                onClick={() => setCategory("accessibility")}
                label={`Accessibility (${categoryCounts.accessibility})`}
              />
            </div>

            <ul className="action-tracker-list">
              {filtered.map(({ card, sectionTitle }) => {
                const sev = (card.severity || "low").toLowerCase();
                const action = actionsByCardId.get(card.id);
                return (
                  <li key={card.id} className="action-tracker-item" id={`action-${card.id}`}>
                    <div className="action-tracker-item-head">
                      <span className={`action-tracker-sev action-tracker-sev-${sev}`}>
                        {SEVERITY_LABEL[sev] ?? sev}
                      </span>
                      <div className="action-tracker-item-body">
                        <div className="action-tracker-item-title">
                          {card.title || "Finding"}
                        </div>
                        <div className="action-tracker-item-meta">
                          {card.surface_id && (
                            <>
                              <code className="action-tracker-ref">
                                {card.surface_id}
                              </code>
                              <span className="muted">·</span>
                            </>
                          )}
                          <span className="action-tracker-section">{sectionTitle}</span>
                          {card.target_display && (
                            <>
                              <span className="muted">·</span>
                              <span className="action-tracker-target">
                                {card.target_display}
                              </span>
                            </>
                          )}
                          {action && (
                            <>
                              <span className="muted">·</span>
                              <span className="action-tracker-current-state">
                                {STATE_LABEL[action.state]}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    {card.description && (
                      <p className="action-tracker-item-desc">{card.description}</p>
                    )}
                    <TakeActionPanel
                      evidenceCardId={card.id}
                      caseId={caseId!}
                      currentState={action?.state ?? null}
                      currentNote={action?.note ?? null}
                      currentFixByDate={action?.fix_by_date ?? null}
                    />
                    {action?.state === "fixed" && (
                      <VerifyFixBlock
                        evidenceCardId={card.id}
                        caseId={caseId!}
                        verifiedAt={action.verified_at ?? null}
                      />
                    )}
                    <CommentThread
                      evidenceCardId={card.id}
                      cacheKeyExtra={caseId!}
                    />
                  </li>
                );
              })}
            </ul>

            {filtered.length === 0 && (
              <p className="muted">
                No findings in this category. Switch to a different filter
                or {category !== "all" && (
                  <button
                    type="button"
                    className="action-tracker-link-btn"
                    onClick={() => setCategory("all")}
                  >
                    see all categories
                  </button>
                )}.
              </p>
            )}
          </>
        )}

        {counts.total === 0 && (
          <div className="action-tracker-empty">
            <p>
              This report had no findings in the severity bands that need
              remediation decisions. Informational results (subdomain
              inventory, technology fingerprints, public records) are in
              the full report.
            </p>
            <Link
              to={`/portal/engagements/${caseId}/reports/${reportId}/html`}
              className="recon-btn recon-btn-secondary"
            >
              View full report →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className={`action-tracker-stat action-tracker-stat-${tone}`}>
      <span className="action-tracker-stat-value">{value}</span>
      <span className="action-tracker-stat-label">{label}</span>
    </div>
  );
}

function FilterPill({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={`action-tracker-pill${active ? " is-active" : ""}`}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

/**
 * Verify-fix block (Bucket QP-W1).
 *
 * Two visual states:
 *   - Already verified: small "Verified <date>" badge, no action.
 *   - Claimed-fixed-but-not-verified: short copy + "Request verification"
 *     button that kicks off a re-test. The button reuses the original
 *     scan tool against the same target; on completion the backend
 *     compares finding_identity_hash and stamps verified_at when the
 *     fix held.
 *
 * On click, we POST to verify-fix, store a transient status, and
 * invalidate the finding-actions query so the latest verified state
 * shows up the next time the buyer refreshes. The re-test itself runs
 * asynchronously in a backend BackgroundTask — the buyer sees
 * "Verification queued" and can refresh later to see the result.
 */
function VerifyFixBlock({
  evidenceCardId,
  caseId,
  verifiedAt,
}: {
  evidenceCardId: string;
  caseId: string;
  verifiedAt: string | null;
}) {
  const queryClient = useQueryClient();
  const [submittedMessage, setSubmittedMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      const { data, error } =
        await requestFindingVerificationApiV1ClientPortalFindingsEvidenceCardIdVerifyFixPost(
          { path: { evidence_card_id: evidenceCardId } },
        );
      if (error) {
        // Pydantic 422 surfaces { detail: "..." }; fall back to a stock copy.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const detail = (error as any)?.detail;
        throw new Error(
          typeof detail === "string"
            ? detail
            : "Couldn't queue the verification re-test.",
        );
      }
      return data;
    },
    onSuccess: (data) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const msg = (data as any)?.message as string | undefined;
      setSubmittedMessage(
        msg ?? "Verification queued. Refresh in a few minutes to see the result.",
      );
      setErrorMessage(null);
      // Invalidate so the next refetch picks up verified_at if it lands fast.
      queryClient.invalidateQueries({ queryKey: ["finding-actions", caseId] });
    },
    onError: (err: Error) => {
      setErrorMessage(err.message);
      setSubmittedMessage(null);
    },
  });

  if (verifiedAt) {
    const when = new Date(verifiedAt);
    const display = isNaN(when.getTime())
      ? "Verified"
      : `Verified · ${when.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
    return (
      <div className="action-tracker-verify">
        <span
          className="action-tracker-verified-badge"
          role="status"
          aria-label={`Verification confirmed — ${display}`}
        >
          <span aria-hidden="true">✓</span> {display}
        </span>
        <p className="action-tracker-verify-copy">
          The assessor re-ran the original detection and confirmed this
          finding no longer reproduces.
        </p>
      </div>
    );
  }

  return (
    <div className="action-tracker-verify">
      <p className="action-tracker-verify-copy">
        <strong>Mark fixed — ready to confirm?</strong> Requesting verification
        re-runs the original detection against the same target. Free, and
        takes a few minutes.
      </p>
      <button
        type="button"
        className="action-tracker-verify-btn"
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
      >
        {mutation.isPending ? "Queuing…" : "Verify this fix →"}
      </button>
      {/*
        Status messages live in a polite live region so a screen reader
        announces the result of clicking the button. role="status" keeps
        the region present in the DOM at all times so the announcement
        fires whether the message appears or disappears.
      */}
      <div
        className="action-tracker-verify-status-region"
        role="status"
        aria-live="polite"
      >
        {submittedMessage && (
          <span className="action-tracker-verify-status is-verified">
            <span aria-hidden="true">✓ </span>
            {submittedMessage}
          </span>
        )}
        {errorMessage && (
          <span className="action-tracker-verify-status is-error">
            <span aria-hidden="true">⚠ </span>
            {errorMessage}
          </span>
        )}
      </div>
    </div>
  );
}
